<template>
  <AdminLayout>
    <div class="export-management">
      <h2>导出文件管理</h2>

      <!-- 激活记录 -->
      <section class="activation-records-section">
        <h3>激活记录</h3>

        <div class="activation-search flex gap-sm align-items-center">
          <input
            type="text"
            v-model="searchKeyword"
            placeholder="按卡密搜索激活记录"
            @keyup.enter="searchRecords"
            class="form-control"
            style="flex: 1; max-width: 300px;"
          />
          <button @click="searchRecords" class="btn btn-primary">
            搜索
          </button>
          <button @click="clearSearch" class="btn btn-secondary">
            清空
          </button>
          <button @click="loadActivationRecords" class="btn btn-success" :disabled="loading">
            刷新
          </button>
        </div>

        <!-- 移动端卡片式布局 -->
        <div class="mobile-records" v-if="isMobile">
          <div v-if="loading" class="mobile-loading">
            正在加载激活记录数据...
          </div>
          <div v-else-if="filteredRecords.length === 0" class="mobile-empty">
            <p>暂无激活记录数据</p>
          </div>
          <div v-else class="mobile-record-list">
            <div v-for="(record, index) in filteredRecords" :key="record.id || index" class="mobile-record-item">
              <div class="mobile-record-header">
                <div class="record-title">导出记录</div>
                <span class="status-badge" :class="getExportStatusClass(record.exportStatus)">
                  {{ getExportStatusText(record.exportStatus) }}
                </span>
              </div>
              <div class="mobile-record-content">
                <div class="mobile-record-row">
                  <span class="label">卡密:</span>
                  <code class="card-code">{{ record.cardCode }}</code>
                </div>
                <div class="mobile-record-row">
                  <span class="label">绑定邮箱:</span>
                  <div class="email-with-copy" v-if="record.email">
                    <a :href="'mailto:' + record.email" class="link-style">{{ record.email }}</a>
                    <button class="btn-copy" @click.stop="copyText(record.email, '邮箱')" title="复制邮箱">
                      <img src="@/assets/copy.png" alt="复制" class="copy-icon" />
                    </button>
                  </div>
                  <span v-else>-</span>
                </div>
                <div class="mobile-record-row">
                  <span class="label">提交URL:</span>
                  <div class="url-with-copy" v-if="record.fileUrl">
                    <a :href="record.fileUrl" target="_blank" class="link-url">{{ record.fileUrl }}</a>
                    <button class="btn-copy" @click="copyText(record.fileUrl)" title="复制链接">
                      <img src="@/assets/copy.png" alt="复制" class="copy-icon" />
                    </button>
                  </div>
                  <span v-else>-</span>
                </div>
                <div class="mobile-record-row">
                  <span class="label">提交时间:</span>
                  <span>{{ formatDate(record.submitTime) }}</span>
                </div>
                <div class="mobile-record-row">
                  <span class="label">完成时间:</span>
                  <span>{{ formatDate(record.completeTime) }}</span>
                </div>
                <div class="mobile-record-row" v-if="record.expireTime">
                  <span class="label">过期时间:</span>
                  <span>{{ formatDate(record.expireTime) }}</span>
                </div>
                <div class="mobile-record-row" v-if="record.message">
                  <span class="label">消息:</span>
                  <span>{{ record.message }}</span>
                </div>
                <div class="mobile-record-row">
                  <span class="label">下载:</span>
                  <a v-if="record.downloadUrl && record.exportStatus === 'completed'" :href="record.downloadUrl" target="_blank" class="link-url">下载</a>
                  <span v-else class="text-muted">-</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 桌面端表格布局 -->
        <AdminTable
          v-else
          :columns="recordColumns"
          :data="filteredRecords"
          :total="totalRecords"
          :current-page="recordCurrentPage"
          :page-size="recordPageSize"
          :row-key="getRecordId"
          max-height="500px"
        >
          <template #cardCode="{ item }">
            <code class="card-code">{{ item.cardCode }}</code>
          </template>

          <template #email="{ item }">
            <div class="email-with-copy" v-if="item.email">
              <a :href="'mailto:' + item.email" class="link-style">{{ item.email }}</a>
              <button class="btn-copy" @click.stop="copyText(item.email, '邮箱')" title="复制邮箱">
                <img src="@/assets/copy.png" alt="复制" class="copy-icon" />
              </button>
            </div>
            <span v-else>-</span>
          </template>

          <template #fileUrl="{ item }">
            <div class="url-with-copy" v-if="item.fileUrl">
              <a :href="item.fileUrl" target="_blank" class="link-url">{{ item.fileUrl }}</a>
              <button class="btn-copy" @click="copyText(item.fileUrl)" title="复制链接">
                <img src="@/assets/copy.png" alt="复制" class="copy-icon" />
              </button>
            </div>
            <span v-else>-</span>
          </template>

          <template #submitTime="{ item }">
            {{ formatDate(item.submitTime) }}
          </template>

          <template #completeTime="{ item }">
            {{ formatDate(item.completeTime) }}
          </template>

          <template #expireTime="{ item }">
            {{ formatDate(item.expireTime) }}
          </template>

          <template #format="{ item }">
            {{ item.format || '-' }}
          </template>

          <template #exportStatus="{ item }">
            <span class="status-badge" :class="getExportStatusClass(item.exportStatus)">
              {{ getExportStatusText(item.exportStatus) }}
            </span>
            <div v-if="item.message" class="export-message">{{ item.message }}</div>
          </template>

          <template #downloadUrl="{ item }">
            <a v-if="item.downloadUrl && item.exportStatus === 'completed'" :href="item.downloadUrl" target="_blank" class="link-url">下载</a>
            <span v-else class="text-muted">-</span>
          </template>
        </AdminTable>

        <!-- 激活记录分页 -->
        <AdminPagination
          :current-page="recordCurrentPage"
          :page-size="recordPageSize"
          :total="totalRecords"
          @update:currentPage="recordCurrentPage = $event; loadActivationRecords()"
          @update:pageSize="recordPageSize = $event; recordCurrentPage = 1; loadActivationRecords()"
        />
      </section>

      <!-- BioRender管理员账户配置 -->
      <section class="admin-config-section">
        <h3>BioRender管理员账户配置</h3>

        <!-- 添加新管理员账户 -->
        <div class="add-admin-section">
          <h4>添加新管理员账户</h4>

          <!-- 单个添加表单 -->
          <form @submit.prevent="addAdmin" class="add-admin-form">
            <div class="flex gap-lg align-items-end">
              <div class="form-group" style="flex: 1;">
                <label>管理员邮箱</label>
                <input
                  type="email"
                  v-model="newAdmin.email"
                  placeholder="请输入BioRender管理员邮箱"
                  class="form-control"
                  required
                />
              </div>
              <div class="form-group" style="flex: 1;">
                <label>管理员密码</label>
                <div class="password-input">
                  <input
                    :type="showNewPassword ? 'text' : 'password'"
                    v-model="newAdmin.password"
                    placeholder="请输入BioRender管理员密码"
                    class="form-control"
                    required
                  />
                  <button
                    type="button"
                    class="password-toggle"
                    @click="showNewPassword = !showNewPassword"
                  >
                    {{ showNewPassword ? '👁️' : '👁️‍🗨️' }}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                :disabled="!newAdmin.email || !newAdmin.password || loading"
                class="btn btn-success"
              >
                添加账户
              </button>
            </div>
          </form>
        </div>

        <!-- 当前激活账户信息 -->
        <div class="current-account-info" v-if="currentActiveAccount">
          <h4>当前激活账户</h4>
          <div class="current-account-card">
            <div class="account-info flex gap-xl">
              <div class="info-item">
                <label>邮箱:</label>
                <span>{{ currentActiveAccount.email }}</span>
              </div>
              <div class="info-item">
                <label>状态:</label>
                <span class="status-badge" :class="getStatusClass(currentActiveAccount.status)">
                  {{ getAccountStatusText(currentActiveAccount.status) }}
                </span>
              </div>
            </div>
            <div class="account-actions">
              <button
                v-if="(currentActiveAccount.activatedUsersCount || 0) >= 9"
                @click="switchToNextAvailableAccount"
                class="btn btn-warning"
                :disabled="!hasAvailableAccounts"
              >
                切换到下一个可用账户
              </button>
            </div>
          </div>
        </div>

        <!-- 无激活账户提示 -->
        <div class="no-account-info" v-else>
          <h4>当前激活账户</h4>
          <div class="no-account-card">
            <p class="no-account-text">暂无激活的管理员账户，请先添加账户</p>
          </div>
        </div>
      </section>

      <!-- 导出专用卡密管理 -->
      <CardManagementTable
        section-title="卡密列表"
        card-type-filter="export"
        :show-card-type-select="false"
        :show-member-type-select="false"
        :show-batch-count="true"
        :hide-export="true"
        create-button-text="创建卡密"
        create-dialog-title="创建卡密"
        @message="handleCardMessage"
      />

      <div v-if="message" class="message" :class="messageType">
        {{ message }}
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import AdminLayout from '@/components/admin/AdminLayout.vue'
import AdminTable from '@/components/admin/AdminTable.vue'
import AdminPagination from '@/components/admin/AdminPagination.vue'
import CardManagementTable from '@/components/admin/CardManagementTable.vue'

export default {
  name: 'ExportManagement',
  components: {
    AdminLayout,
    AdminTable,
    AdminPagination,
    CardManagementTable
  },
  data() {
    return {
      loading: false,
      message: '',
      messageType: 'success',
      newAdmin: {
        email: '',
        password: ''
      },
      showNewPassword: false,
      adminAccounts: [], // 独立的导出管理账户列表，初始为空
      searchKeyword: '',
      originalRecords: [], // 原始激活记录数据
      totalRecords: 0, // 激活记录总数
      recordCurrentPage: 1, // 激活记录当前页
      recordPageSize: 10, // 激活记录每页大小
      isMobile: false,
      recordColumns: [
        { key: 'cardCode', title: '卡密' },
        { key: 'email', title: '绑定邮箱' },
        { key: 'fileUrl', title: '提交URL' },
        { key: 'format', title: '文件类型' },
        { key: 'submitTime', title: '提交时间' },
        { key: 'completeTime', title: '完成时间' },
        { key: 'expireTime', title: '过期时间' },
        { key: 'exportStatus', title: '导出结果' },
        { key: 'downloadUrl', title: '下载' }
      ]
    }
  },
  computed: {
    currentActiveAccount() {
      return this.adminAccounts.find(account => account.isActive)
    },
    hasAvailableAccounts() {
      return this.adminAccounts.some(account =>
        !account.isActive && this.getStatusClass(account.status) === 'status-available'
      )
    },
    filteredRecords() {
      if (!this.searchKeyword) {
        return this.originalRecords
      }
      return this.originalRecords.filter(record =>
        record.cardCode.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
        record.automationLoginEmail.toLowerCase().includes(this.searchKeyword.toLowerCase())
      )
    }
  },
  mounted() {
    // 加载导出管理的独立账户列表
    this.loadAdminAccounts()
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    this.loadActivationRecords()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    async copyText(text, type = '链接') {
      try {
        await navigator.clipboard.writeText(text)
        alert(`${type}已复制到剪贴板`)
      } catch (err) {
        // 降级方案
        const input = document.createElement('input')
        input.value = text
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        alert(`${type}已复制到剪贴板`)
      }
    },

    async loadActivationRecords() {
      try {
        // 复用卡密管理的激活记录接口，保持分页查询
        const { getActivations } = await import('@/api/cards')
        const response = await getActivations({
          current: this.recordCurrentPage || 1,
          size: this.recordPageSize || 10,
          type: 'export' // 筛选export类型的激活记录
        })

        if (response.code === 200) {
          if (response.data?.activations) {
            this.originalRecords = response.data.activations || []
            this.totalRecords = response.data.total || 0
          } else {
            this.originalRecords = response.data || []
            this.totalRecords = this.originalRecords.length
          }
        } else {
          console.error('加载激活记录失败:', response.message)
          this.originalRecords = []
          this.totalRecords = 0
        }
      } catch (error) {
        console.error('加载激活记录失败:', error)
        this.originalRecords = []
        this.totalRecords = 0
      }
    },

    searchRecords() {
      // 搜索功能已通过computed属性filteredRecords实现
      console.log('搜索关键词:', this.searchKeyword)
    },

    clearSearch() {
      this.searchKeyword = ''
    },

    getRecordId(record, index) {
      return record.id || index
    },

    isExpired(record) {
      if (!record.expireTime) return false
      return new Date(record.expireTime) < new Date()
    },

    getRecordStatusClass(record) {
      if (this.isExpired(record)) {
        return 'status-expired'
      }
      return 'status-active'
    },

    getExportStatusClass(status) {
      const statusClassMap = {
        'pending': 'status-pending',
        'processing': 'status-processing',
        'completed': 'status-completed',
        'failed': 'status-failed'
      }
      return statusClassMap[status] || 'status-pending'
    },

    getExportStatusText(status) {
      const statusMap = {
        'pending': '待处理',
        'processing': '处理中',
        'completed': '成功',
        'failed': '失败'
      }
      return statusMap[status] || status
    },

    getRecordStatusText(record) {
      if (this.isExpired(record)) {
        return '已过期'
      }
      return '有效'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('zh-CN')
    },

    async addAdmin() {
      if (!this.newAdmin.email || !this.newAdmin.password) {
        this.showMessage('请填写完整的邮箱和密码', 'error')
        return
      }

      this.loading = true

      try {
        const response = await fetch('/api/admin/accounts', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: this.newAdmin.email,
            password: this.newAdmin.password,
            accountType: 'export', // 设置为导出管理类型
            status: 1,
            maxActivationLimit: 100,
            isActive: true // 导出账户创建时总是设置为激活状态，后端会处理之前账户的失效
          })
        })

        const result = await response.json()

        if (result.code === 200) {
          // 添加成功后，重新加载账户列表
          await this.loadAdminAccounts()
          this.newAdmin = { email: '', password: '' }
          this.showMessage('管理员账户添加成功', 'success')
          alert('管理员账户添加成功')
        } else {
          this.showMessage('添加失败: ' + result.message, 'error')
          alert('添加失败: ' + result.message)
        }
      } catch (error) {
        console.error('添加管理员账户失败:', error)
        this.showMessage('添加失败: ' + error.message, 'error')
        alert('添加失败: ' + error.message)
      } finally {
        this.loading = false
      }
    },

    async loadAdminAccounts() {
      try {
        const response = await fetch('/api/admin/accounts?accountType=export&current=1&size=100')
        const result = await response.json()

        if (result.code === 200) {
          this.adminAccounts = result.data.records || []
        } else {
          console.error('加载管理员账户失败:', result.message)
          this.adminAccounts = []
        }
      } catch (error) {
        console.error('加载管理员账户失败:', error)
        this.adminAccounts = []
      }
    },

    async switchToNextAvailableAccount() {
      this.loading = true

      try {
        const response = await fetch('/api/admin/accounts/switch?accountType=export', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        })

        const result = await response.json()

        if (result.code === 200) {
          // 重新加载账户列表以获取最新状态
          await this.loadAdminAccounts()
          this.showMessage('已自动切换到下一个可用账户', 'success')
        } else {
          this.showMessage('切换失败: ' + result.message, 'error')
        }
      } catch (error) {
        console.error('切换账户失败:', error)
        this.showMessage('切换失败: ' + error.message, 'error')
      } finally {
        this.loading = false
      }
    },

    getStatusClass(status) {
      const statusClassMap = {
        1: 'status-available',
        2: 'status-full',
        3: 'status-disabled',
        4: 'status-error',
        'available': 'status-available',
        'full': 'status-full',
        'disabled': 'status-disabled',
        'error': 'status-error'
      }
      return statusClassMap[status] || 'status-available'
    },

    getAccountStatusText(status) {
      const statusMap = {
        1: '可用',
        2: '已满',
        3: '已禁用',
        4: '异常',
        'available': '可用',
        'full': '已满',
        'disabled': '已禁用',
        'error': '异常'
      }
      return statusMap[status] || '可用'
    },

    showMessage(text, type = 'success') {
      this.message = text
      this.messageType = type
      setTimeout(() => {
        this.message = ''
      }, 3000)
    },

    handleCardMessage(messageData) {
      this.showMessage(messageData.text, messageData.type)
    }
  }
}
</script>

<style scoped>
@import '@/styles/admin-common.css';

.export-management h2 {
  text-align: center;
  font-size: var(--font-size-xxl);
  margin-bottom: var(--spacing-xxl);
  color: var(--text-primary);
}

.export-management h3 {
  font-size: var(--font-size-xl);
  border-bottom: 2px solid var(--border-color);
  padding-bottom: var(--spacing-sm);
  margin-bottom: var(--spacing-xl);
  color: var(--text-primary);
}

.export-management h4 {
  font-size: var(--font-size-lg);
  margin-bottom: var(--spacing-lg);
  color: var(--text-primary);
}

.admin-config-section {
  background: var(--bg-light);
  padding: var(--spacing-xl);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--border-color);
}

.add-admin-section {
  margin-bottom: var(--spacing-xl);
}

.add-admin-form {
  background: var(--bg-primary);
  padding: var(--spacing-xl);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--border-color);
}

.password-input {
  position: relative;
  display: flex;
  align-items: center;
}

.password-toggle {
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--font-size-lg);
  padding: var(--spacing-xs);
  opacity: 0.7;
  transition: opacity var(--transition-fast);
  position: absolute;
  right: var(--spacing-sm);
}

.password-toggle:hover {
  opacity: 1;
}

.current-account-info {
  background: #e3f2fd;
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-xl);
}

.no-account-info {
  background: #f5f5f5;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-xl);
}

.no-account-card {
  text-align: center;
  padding: var(--spacing-lg);
}

.no-account-text {
  color: var(--text-secondary);
  font-style: italic;
  margin: 0;
  font-size: var(--font-size-md);
}

.current-account-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-xl);
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.info-item label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  font-weight: 600;
  margin-bottom: 0;
}

.info-item span {
  font-weight: 500;
}

.activation-records-section {
  margin-bottom: var(--spacing-xxl);
  background: var(--bg-primary);
  padding: var(--spacing-xl);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--border-color);
}

.activation-search {
  margin-bottom: var(--spacing-xl);
}

.card-code {
  background: var(--bg-primary);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-family: monospace;
  font-size: var(--font-size-sm);
  color: var(--primary-color);
  border: 1px solid var(--border-light);
}

.link-url {
  color: var(--primary-color);
  text-decoration: underline;
  word-break: break-all;
  font-size: var(--font-size-sm);
}

.link-url:hover {
  opacity: 0.75;
}

/* 邮箱样式 */
.link-style {
  color: var(--primary-color);
  text-decoration: underline;
  word-break: break-all;
}

.url-with-copy {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
}

.url-with-copy .link-url {
  flex: 1;
}

/* 邮箱复制区域样式 */
.email-with-copy {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.btn-copy {
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--spacing-xxs);
  opacity: 0.6;
  transition: opacity var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-copy:hover {
  opacity: 1;
}

.copy-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.status-pending {
  background-color: #fff3cd;
  color: #856404;
}

.status-processing {
  background-color: #d1ecf1;
  color: #0c5460;
}

.status-completed {
  background-color: var(--bg-success);
  color: var(--text-success);
}

.status-failed {
  background-color: var(--bg-danger);
  color: var(--text-danger);
  font-weight: 600;
}

.text-muted {
  color: var(--text-muted);
}

.export-message {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  margin-top: 2px;
  word-break: break-all;
}

/* 移动端激活记录样式 */
.mobile-records {
  display: block;
}

.mobile-loading, .mobile-empty {
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-secondary);
  font-style: italic;
}

.mobile-record-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.mobile-record-item {
  background: var(--bg-light);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.mobile-record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-sm);
  padding-bottom: var(--spacing-sm);
  border-bottom: 1px solid var(--border-light);
}

.record-email {
  font-weight: 600;
  color: var(--text-primary);
  font-size: var(--font-size-md);
}

.mobile-record-content {
  margin-bottom: var(--spacing-md);
}

.mobile-record-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xs) 0;
  border-bottom: 1px solid var(--border-lighter);
}

.mobile-record-row:last-child {
  border-bottom: none;
}

.mobile-record-row .label {
  font-weight: 600;
  color: var(--text-secondary);
  flex: 0 0 auto;
  min-width: 100px;
}

@media (max-width: 768px) {
  .add-admin-form .flex {
    flex-direction: column;
    align-items: stretch;
  }

  .current-account-card {
    flex-direction: column;
    align-items: stretch;
  }

  .account-info {
    flex-direction: column;
    gap: var(--spacing-lg);
  }

  .activation-search {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-sm);
  }

  .activation-search input {
    flex: none;
    max-width: none !important;
  }

  .activation-search .btn {
    width: 100%;
  }

  /* 卡密代码在移动端的处理 */
  .card-code {
    font-size: var(--font-size-xs);
    padding: var(--spacing-xxs) var(--spacing-xs);
  }
}
</style>